import React, { useEffect, useMemo, useRef, useState } from "react";
import { CreepCharacter } from "@/components/CreepCharacter";

/**
 * DonationSuccessAnimation — post-donation Creep celebration.
 * Overlays the whole viewport with a soft violet vignette, snow flurry,
 * and a Creep in "happy" pose surrounded by orbiting particles. Not
 * intrusive — auto-dismisses in ~6 seconds (or on click).
 */
export const DonationSuccessAnimation = ({ hash, onClose }) => {
  const [visible, setVisible] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      setVisible(false);
      setTimeout(() => onClose?.(), 500);
    }, 6500);
    return () => clearTimeout(timerRef.current);
  }, [onClose]);

  const flakes = useMemo(
    () => Array.from({ length: 36 }).map((_, i) => ({
      left: Math.random() * 100,
      size: 2 + Math.random() * 5,
      delay: Math.random() * 3,
      duration: 5 + Math.random() * 6,
      opacity: 0.4 + Math.random() * 0.5,
      key: i,
    })),
    [],
  );

  const orbits = useMemo(
    () => Array.from({ length: 14 }).map((_, i) => ({
      angle: (i * 360) / 14,
      radius: 130 + (i % 3) * 22,
      color: ["#5B2EFF", "#8A6BFF", "#D6002F"][i % 3],
      size: 3 + (i % 4),
      duration: 4 + (i % 5),
      key: i,
    })),
    [],
  );

  return (
    <div
      data-testid="donation-success"
      className={`fixed inset-0 z-[70] flex items-center justify-center transition-opacity duration-500 ${
        visible ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      style={{
        background:
          "radial-gradient(650px 500px at 50% 55%, rgba(91,46,255,0.55), transparent 65%), radial-gradient(500px 350px at 50% 40%, rgba(214,0,47,0.18), transparent 60%), rgba(11,11,18,0.75)",
        backdropFilter: "blur(6px)",
      }}
      onClick={() => {
        setVisible(false);
        setTimeout(() => onClose?.(), 400);
      }}
    >
      {/* Snowflakes */}
      <div className="absolute inset-0 pointer-events-none">
        {flakes.map((f) => (
          <span
            key={f.key}
            className="absolute rounded-full bg-white"
            style={{
              left: `${f.left}%`,
              top: "-8px",
              width: f.size,
              height: f.size,
              opacity: f.opacity,
              filter: "blur(0.4px)",
              animation: `creep-snowfall ${f.duration}s linear ${f.delay}s infinite`,
            }}
          />
        ))}
      </div>

      {/* Creep + orbiting particles */}
      <div className="relative">
        <div className="relative">
          <CreepCharacter size={280} pose="happy" accessories={["scarf", "goggles", "snowboard", "cloak"]} />

          {/* Orbiting particles */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ animation: "creep-orbit-rot 8s linear infinite" }}
          >
            {orbits.map((o) => {
              const rad = (o.angle * Math.PI) / 180;
              const x = 50 + (o.radius / 280) * 100 * Math.cos(rad);
              const y = 50 + (o.radius / 280) * 100 * Math.sin(rad);
              return (
                <span
                  key={o.key}
                  className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full"
                  style={{
                    left: `${x}%`,
                    top: `${y}%`,
                    width: o.size,
                    height: o.size,
                    background: o.color,
                    boxShadow: `0 0 ${o.size * 4}px ${o.color}, 0 0 ${o.size * 8}px ${o.color}55`,
                    animation: `creep-float-a ${o.duration}s ease-in-out infinite`,
                  }}
                />
              );
            })}
          </div>
        </div>

        {/* Message */}
        <div className="mt-6 max-w-md text-center px-4">
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#8A6BFF] mb-2">Donación confirmada</div>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">
            <span className="text-[#D6002F]">❤</span> Gracias por apoyar Esquiada
          </h2>
          <p className="mt-3 text-sm text-white/75 leading-relaxed">
            Cada contribución nos ayuda a seguir mejorando la app para toda la comunidad de esquiadores.
          </p>
          {hash && (
            <a
              data-testid="donation-success-tx"
              href={`https://bscscan.com/tx/${hash}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-block text-xs text-[#8A6BFF] hover:text-white underline underline-offset-4"
              onClick={(e) => e.stopPropagation()}
            >
              Ver transacción en BscScan ↗
            </a>
          )}
          <div className="mt-6 text-[10px] uppercase tracking-widest text-white/40">
            Toca en cualquier lugar para cerrar
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonationSuccessAnimation;
